import { useCallback } from 'react';
import Image from 'next/image';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ArrowLeft, ArrowRight, Quote } from 'lucide-react';
import { testimonials } from '@/lib/aboutPage';

const Testimonials = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' }, [
    Autoplay({ delay: 5000, stopOnInteraction: true, stopOnMouseEnter: true })
  ]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  const getInitials = (name = '') => {
    if (typeof name !== 'string' || !name.trim()) return '?';
    return name.trim().split(/\s+/).map(word => word[0]).join('').toUpperCase();
  };

  return (
    <section 
      id="testimonials"
      className="relative w-full flex items-center overflow-hidden min-h-[600px] bg-[#001D4A]"
      aria-labelledby="testimonials-heading"
    >
      {/* Background Image - Performance Optimized */}
      <div className="absolute inset-0 z-0">
        <Image
          src="/images/about1.jpg"
          alt="" // Decorative background
          fill
          className="object-cover"
          sizes="100vw"
          loading="lazy" // Not top-of-page, so lazy load
          quality={80}
        />
        <div className="absolute inset-0 bg-[#001D4A]/90 lg:bg-[#001D4A]/85 mix-blend-multiply" aria-hidden="true" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-12 md:py-20 lg:py-32">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 xl:gap-24">

          {/* Carousel Card */}
          <div className="w-full lg:w-1/2 bg-white p-6 sm:p-10 md:p-12 lg:p-16 shadow-2xl rounded-sm order-2 lg:order-1 border-b-4 border-[#4F7AC2]">
            <div className="flex justify-between items-center mb-6 md:mb-8">
              <Quote 
                size={32} 
                className="text-[#4F7AC2] opacity-20 fill-current md:scale-125" 
                aria-hidden="true" 
              />
              <div className="flex gap-2">
                <button 
                  onClick={scrollPrev} 
                  aria-label="Previous testimonial"
                  className="w-10 h-10 md:w-11 md:h-11 rounded-full border border-gray-100 flex items-center justify-center text-[#4F7AC2] hover:bg-[#4F7AC2] hover:text-white transition-all active:scale-90 focus-visible:ring-2 focus-visible:ring-[#4F7AC2] outline-none"
                >
                  <ArrowLeft size={18} />
                </button>
                <button 
                  onClick={scrollNext} 
                  aria-label="Next testimonial"
                  className="w-10 h-10 md:w-11 md:h-11 rounded-full border border-gray-100 flex items-center justify-center text-[#4F7AC2] hover:bg-[#4F7AC2] hover:text-white transition-all active:scale-90 focus-visible:ring-2 focus-visible:ring-[#4F7AC2] outline-none"
                >
                  <ArrowRight size={18} />
                </button>
              </div>
            </div>

            {/* Carousel Region */}
            <div 
              className="overflow-hidden cursor-grab active:cursor-grabbing" 
              ref={emblaRef}
              role="region"
              aria-roledescription="carousel"
              aria-live="polite"
            >
              <div className="flex">
                {testimonials.clients.map((item) => (
                  <div 
                    key={item.id} 
                    className="flex-[0_0_100%] min-w-0 pr-2 sm:pr-4"
                    role="group"
                    aria-roledescription="slide"
                  >
                    <blockquote className="m-0">
                      <p className="text-[#001D4A] italic text-base sm:text-lg md:text-xl leading-relaxed mb-6 md:mb-10 font-medium">
                        &ldquo;{item.text}&rdquo;
                      </p>

                      <footer className="flex items-center gap-3 sm:gap-5 pt-6 border-t border-gray-100">
                        <div 
                          className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-gradient-to-br from-[#4F7AC2] to-[#001D4A] flex-shrink-0 flex items-center justify-center text-white font-bold text-sm md:text-lg shadow-md"
                          aria-hidden="true"
                        >
                          {getInitials(item.title)}
                        </div>

                        <div className="flex flex-col min-w-0">
                          <cite className="not-italic font-extrabold text-[#001D4A] text-base md:text-lg lg:text-xl tracking-tight leading-tight truncate">
                            {item.title}
                          </cite>
                          <span className="text-[#4F7AC2] text-[10px] md:text-xs font-black uppercase tracking-widest mt-0.5">
                            {item.firm}
                          </span>
                          <div className="mt-1.5">
                            <span className="inline-block bg-[#4F7AC2]/10 text-[#4F7AC2] text-[9px] md:text-[10px] px-2 py-0.5 rounded font-bold uppercase">
                              {item.service}
                            </span>
                          </div>
                        </div>
                      </footer>
                    </blockquote>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar Content */}
          <div className="w-full lg:w-1/2 text-white text-center lg:text-left order-1 lg:order-2">
            <header>
              <div className="flex items-center justify-center lg:justify-start gap-2 text-[#4F7AC2] font-bold mb-3 md:mb-5">
                <span className="text-xl" aria-hidden="true">»</span>
                <span className="uppercase tracking-[0.2em] text-[10px]">Client Feedback</span>
              </div>

              <h2 
                id="testimonials-heading"
                className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold mb-4 md:mb-8 leading-[1.1]"
              >
                {testimonials.title}
              </h2>
            </header>

            <p className="text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed mb-6 md:mb-10 max-w-xl mx-auto lg:mx-0">
              {testimonials.description}
            </p>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Testimonials;